'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';

import carouselControllerHydroNode from '@/assets/images/carousel-controller-hydro-node.jpeg';
import carouselNodeDripline from '@/assets/images/carousel-node-dripline.jpeg';
import carouselPyranometer from '@/assets/images/carousel-pyranometer.jpeg';
import carouselControllerTouchscreen from '@/assets/images/carousel-controller-touchscreen.jpeg';
import carouselCranberry from '@/assets/images/carousel-cranberry.jpeg';
import carouselDrainBuckets from '@/assets/images/carousel-drain-buckets.jpeg';
import carouselNodeSubstrate from '@/assets/images/carousel-node-substrate.jpeg';
import carouselIrTemp from '@/assets/images/carousel-ir-temp.jpeg';
import carouselSmartIrrigationMountains from '@/assets/images/carousel-smart-irrigation-mountains.jpg';
import carouselSubstrateRootZoneSensor from '@/assets/images/carousel-substrate-root-zone-sensor.jpg';

const galleryImages = [
  {
    src: carouselNodeDripline,
    title: 'Field sensing for blueberries',
    description: 'Solar-powered sensing node placed in every zone with a pressure sensor and three soil water potential sensors.',
    alt: 'Ensemble field hardware installed beside drip irrigation in a blueberry row',
  },
  {
    src: carouselCranberry,
    title: 'Field sensing for cranberries',
    description: 'Sensing node with temperature, humidity and soil moisture monitoring for frost protection and automated irrigation.',
    alt: 'Solar-powered Ensemble monitoring station in a cranberry field',
  },
  {
    src: carouselNodeSubstrate,
    title: 'Field sensing for substrate-grown strawberries',
    description: 'Climate, root-zone and drip/drain sensing in a polytunnel environment.',
    alt: 'Sensors and Ensemble field hardware installed in substrate-grown strawberries',
  },
  {
    src: carouselPyranometer,
    title: 'Pyranometer and weather station',
    description: 'Light and weather data captured in the field, not miles away.',
    alt: 'Light and weather sensors installed beside a berry field',
  },
  {
    src: carouselControllerTouchscreen,
    title: 'Irrigation controller for blueberry driplines',
    description: 'HydroPulse 2-Wire controller with 27 active zones. Create and manage irrigation directly from the controller screen.',
    alt: 'Irrigation controller touchscreen showing an irrigation event',
  },
  {
    src: carouselControllerHydroNode,
    title: 'Irrigation controller for cranberry sprinklers',
    description: 'HydroNode mounted in a pump house with direct monitoring of mainline pressure.',
    alt: 'Ensemble irrigation controller installed on a pump-house wall',
  },
  {
    src: carouselSmartIrrigationMountains,
    title: 'Remote valve control for blueberry driplines',
    description: 'Solar-powered HydroNode operating 9 V latching solenoid valves wirelessly.',
    alt: 'PulseLogic smart irrigation controller installed in a berry field with mountains behind it',
  },
  {
    src: carouselDrainBuckets,
    title: 'DripDrain sensing for substrate-grown strawberries',
    description: 'Purpose-built sensors track the water entering and leaving substrate crops, along with pH, EC and temperature.',
    alt: 'Automated drainage measurement buckets in a strawberry polytunnel',
  },
  {
    src: carouselIrTemp,
    title: 'Leaf-temperature sensing for substrate-grown strawberries',
    description: 'An infrared sensor compares leaf and ambient temperatures to infer photosynthetic activity.',
    alt: 'Infrared crop temperature sensor aimed at strawberry plants',
  },
  {
    src: carouselSubstrateRootZoneSensor,
    title: 'Root-zone sensing for substrate-grown raspberries',
    description: 'Sensors monitor conditions directly inside each pot alongside the irrigation drippers.',
    alt: 'Root-zone sensor and irrigation drippers installed in a substrate crop pot',
  },
];

export default function ImageCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);

  // Lock body scroll when fullscreen is active
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const previousOverflow = document.body.style.overflow;

    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousOverflow || '';
    };
  }, [isFullscreen]);

  // Auto-play carousel
  // useEffect(() => {
  //   if (!isFullscreen) {
  //     const interval = setInterval(() => {
  //       setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
  //     }, 5000); // Change image every 5 seconds

  //     return () => clearInterval(interval);
  //   }
  // }, [isFullscreen]);

  // Keyboard navigation in fullscreen
  useEffect(() => {
    if (isFullscreen) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowLeft') {
          setFullscreenIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
        } else if (e.key === 'ArrowRight') {
          setFullscreenIndex((prev) => (prev + 1) % galleryImages.length);
        } else if (e.key === 'Escape') {
          setIsFullscreen(false);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isFullscreen]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const openFullscreen = (index: number) => {
    setFullscreenIndex(index);
    setIsFullscreen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreen(false);
  };

  const goToPreviousFullscreen = () => {
    setFullscreenIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const goToNextFullscreen = () => {
    setFullscreenIndex((prev) => (prev + 1) % galleryImages.length);
  };

  return (
    <>
      <section className="bg-[#e7efea] py-8 dark:bg-[#1a2924] md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-5 max-w-3xl text-center md:mb-7">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#257676] dark:text-[#72c7c7]">
              Field installations
            </p>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white md:text-4xl">
              Built for working farms
            </h2>
            {/* <p className="mx-auto mt-3 max-w-2xl text-base leading-relaxed text-slate-600 dark:text-slate-300 md:text-lg">
              From open fields to substrate tunnels, Ensemble fits the way each farm already
              operates.
            </p> */}
          </div>

          {/* Main Carousel */}
          <div className="relative max-w-3xl mx-auto">
            {/* Carousel Container */}
            <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-slate-100 shadow-xl dark:bg-[#1a2522] dark:ring-1 dark:ring-white/10">
              <button
                type="button"
                className="relative block h-full w-full cursor-zoom-in text-left"
                onClick={() => openFullscreen(currentIndex)}
                aria-label={`Open ${galleryImages[currentIndex].title} image in fullscreen`}
              >
                <Image
                  src={galleryImages[currentIndex].src}
                  alt={galleryImages[currentIndex].alt}
                  fill
                  sizes="(max-width: 640px) calc(100vw - 32px), (max-width: 1024px) calc(100vw - 48px), 896px"
                  className="object-cover"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 px-5 pb-4 text-white md:px-7 md:pb-6 md:pr-28"
                  aria-live="polite"
                >
                  <p className="text-base font-semibold leading-tight md:text-xl">
                    {galleryImages[currentIndex].title}
                  </p>
                  <p className="mt-1 max-w-2xl text-xs leading-snug text-white/85 sm:text-sm md:text-base">
                    {galleryImages[currentIndex].description}
                  </p>
                </div>
              </button>

              {/* Navigation Arrows */}
              <button
                onClick={goToPrevious}
                className="absolute left-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-2.5 text-slate-900 shadow-lg transition-all duration-300 hover:scale-105 hover:bg-white dark:bg-[#1f2b27]/90 dark:text-slate-100 dark:ring-1 dark:ring-white/10 dark:hover:bg-[#1f2b27] md:left-4"
                aria-label="Previous image"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <button
                onClick={goToNext}
                className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-2.5 text-slate-900 shadow-lg transition-all duration-300 hover:scale-105 hover:bg-white dark:bg-[#1f2b27]/90 dark:text-slate-100 dark:ring-1 dark:ring-white/10 dark:hover:bg-[#1f2b27] md:right-4"
                aria-label="Next image"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              {/* Image Counter */}
              <div className="absolute right-3 top-3 z-10 rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm md:bottom-6 md:right-6 md:top-auto md:text-sm">
                {currentIndex + 1} / {galleryImages.length}
              </div>
            </div>

            {/* Thumbnail Navigation */}
            <div className="mt-4 py-2">
              <div className="grid grid-cols-5 gap-2 px-1 sm:grid-cols-10">
                {galleryImages.map((image, index) => (
                  <button
                    type="button"
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`relative aspect-[4/3] w-full overflow-hidden rounded-lg transition-all duration-300 ${
                      index === currentIndex
                        ? 'scale-105 ring-2 ring-[#257676] ring-offset-2 ring-offset-[#e7efea] dark:ring-[#4dbdbd] dark:ring-offset-[#1a2924]'
                        : 'opacity-65 hover:scale-105 hover:opacity-100'
                    }`}
                    aria-label={`Show ${image.title}`}
                    aria-current={index === currentIndex ? 'true' : undefined}
                  >
                    <span
                      aria-hidden="true"
                      className="absolute inset-0 bg-cover bg-center"
                      style={{ backgroundImage: `url(${image.src.src})` }}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeFullscreen}
        >
          {/* Close Button */}
          <button
            onClick={closeFullscreen}
            className="absolute top-4 right-4 z-[60] text-white hover:text-gray-300 transition-colors p-2"
            aria-label="Close fullscreen"
          >
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Fullscreen Image Container */}
          <div
            className="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative w-full h-full">
              <Image
                src={galleryImages[fullscreenIndex].src}
                alt={galleryImages[fullscreenIndex].alt}
                width={1920}
                height={1440}
                // className="max-w-full max-h-[95vh] object-contain"
                // priority
              />
            </div>

            {/* Fullscreen Navigation Arrows */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPreviousFullscreen();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors p-4"
              aria-label="Previous image"
            >
              <svg
                className="w-10 h-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNextFullscreen();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors p-4"
              aria-label="Next image"
            >
              <svg
                className="w-10 h-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Fullscreen Caption */}
            <div className="absolute bottom-4 left-1/2 w-[min(42rem,calc(100vw-2rem))] -translate-x-1/2 rounded-2xl bg-black/65 px-5 py-3 text-center text-white shadow-lg backdrop-blur-sm md:px-6 md:py-4">
              <div className="flex items-baseline justify-center gap-2">
                <span className="font-semibold md:text-lg">
                  {galleryImages[fullscreenIndex].title}
                </span>
                <span className="shrink-0 text-sm text-white/60">
                  {fullscreenIndex + 1} / {galleryImages.length}
                </span>
              </div>
              <p className="mt-1 text-sm leading-snug text-white/80 md:text-base">
                {galleryImages[fullscreenIndex].description}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
